import './i18n/config' // TASK-167: Initialize i18n before React renders
import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import OpenDocumentNavigator from './components/OpenDocumentNavigator'
import TodayDashboard from './pages/DashboardPage'
import OnboardingFlow from './pages/OnboardingPage'
import LoginPage from './pages/LoginPage'
import TwoFAChallengePage from './pages/TwoFAChallengePage'
import ClientListTest from './components/clients/ClientListTest'
import WebSocketTestPage from './components/debug/WebSocketTestPage'
import { OAuthCallback } from './components/oauth'
import PortfolioDashboard from './services/portfolio-refactor/containers/PortfolioDashboardContainer'
import MyBookView from './components/portfolio/MyBookView' // TASK-205: My Book view
import ClientLevelView from './components/portfolio/ClientLevelView' // TASK-206: Client Level view
import ClientsPage from './pages/ClientsPage'
import ClientDetail from './components/ClientProfile/ClientDetail'
import CompliancePage from './pages/CompliancePage'
import FormBuilderTest from './pages/FormBuilderTest'
import { AccountProvider } from './contexts/AccountContext'
import { LanguageProvider } from './contexts/LanguageContext' // TASK-167: Language context
// TASK-399: FivePhaseWizardWrapper deleted — replaced by PreparationPage
// TASK-164 Phase 2: New pages for UX refinement
import AlertsPage from './pages/AlertsPage'
import DocumentsPage from './pages/DocumentsPage'
import LanguageTestPage from './pages/LanguageTestPage' // TASK-167: Language test page
import SettingsPage from './pages/SettingsPage' // TASK-168: Settings page
import { SearchPage } from './pages/SearchPage' // TASK-265: Unified Search
import TaskListPage from './pages/TaskListPage' // TASK-359: Task Management
import DraftPage from './pages/DraftPage' // TASK-702: open the email draft a task produced
import VerdictPage from './pages/VerdictPage' // TASK-703: read a regulated specialist verdict
import MeetingsRedirect from './pages/MeetingsRedirect' // TASK-666: /meetings -> canvas widget
// TASK-334: Admin Panel
import AdminLayout from './pages/admin/AdminLayout'
import UsersPage from './pages/admin/UsersPage'
import RolesPage from './pages/admin/RolesPage'
import TeamsPage from './pages/admin/TeamsPage'
import AdminSettingsPage from './pages/admin/SettingsPage'
import SecuritySettingsPage from './pages/admin/SecuritySettingsPage'
import AuditPage from './pages/admin/AuditPage'
import AISettingsPage from './pages/admin/AISettingsPage'
import ASRConfigPage from './pages/admin/ASRConfigPage' // TASK-426: ASR provider toggle
import FeaturesPage from './pages/admin/FeaturesPage' // TASK-428: per-tenant cockpit feature flags
import BotsPage from './pages/admin/BotsPage' // TASK-647: meeting bots ops panel
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import AdminOnboardingPage from './pages/onboarding/AdminOnboardingPage'
// TASK-361: Public upload portal
import UploadPortalPage from './pages/public/UploadPortalPage'
// TASK-373: KYC Workspace (split-screen AI-assisted filling)
import KYCWorkspace from './components/kyc/workspace/KYCWorkspace'
// TASK-393: Meeting Flow Simplification
import { PreparationPage } from './components/meeting/preparation'
import { PostMeetingPage } from './components/meeting/postmeeting'
import LiveMeetingPage from './pages/LiveMeetingPage'
import RedirectWithParams from './components/meeting/RedirectWithParams'
import StartMeetingPage from './pages/StartMeetingPage' // TASK-673: start + capture-mode chooser
// TASK-374: Compliance Officer Review
import COReviewQueue from './components/compliance/review/COReviewQueue'
import COReviewWorkspace from './components/compliance/review/COReviewWorkspace'
// TASK-440/450: Document Workbench. One `/workbench/:docId` route dispatches to
// the Word (SuperDoc) or Excel (Univer) editor by document format via
// WorkbenchRouter. Lazy-loaded so the heavy editor bundles are split into their
// own chunks and only fetched on /workbench — scoping any bundling failure to
// that route instead of poisoning the whole app.
const WorkbenchRouter = lazy(() => import('./components/WorkbenchRouter'))

function App() {
  return (
    <AccountProvider>
      <LanguageProvider>
          {/* TASK-640: agent-requested document opens → /workbench/:docId */}
          <OpenDocumentNavigator />
          <Routes>
      <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
      <Route path="/onboarding" element={<OnboardingFlow />} />
      <Route path="/login" element={<LoginPage />} />
      {/* TASK-631: 2FA challenge (public — carries a short-lived challenge token in state) */}
      <Route path="/login/2fa" element={<TwoFAChallengePage />} />
      {/* TASK-632: alias one release — external bookmarks land on the renamed login */}
      <Route path="/demo-login" element={<Navigate to="/login" replace />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/admin-onboarding" element={<AdminOnboardingPage />} />
      <Route path="/auth/oauth/microsoft/callback" element={<OAuthCallback />} />
      {/* TASK-361: Public upload portal (no auth required) */}
      <Route path="/upload/:token" element={<UploadPortalPage />} />
      {/* TASK-334: Admin Panel Routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/users" replace />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="teams" element={<TeamsPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="security" element={<SecuritySettingsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="ai" element={<AISettingsPage />} />
        <Route path="asr" element={<ASRConfigPage />} />
        <Route path="features" element={<FeaturesPage />} />
        <Route path="bots" element={<BotsPage />} /> {/* TASK-647 */}
      </Route>
      <Route path="/*" element={
        <ProtectedRoute>
          <Layout>
            <Routes>
              <Route path="/dashboard" element={<TodayDashboard />} />
              <Route path="/portfolio" element={<PortfolioDashboard />} />
              <Route path="/portfolio/my-book" element={<MyBookView />} /> {/* TASK-205: My Book view */}
              <Route path="/portfolio/client/:clientId" element={<ClientLevelView />} /> {/* TASK-206: Client Level view */}
              <Route path="/portfolio/client/:clientId/portfolio/:portfolioId" element={<PortfolioDashboard />} /> {/* TASK-371: Portfolio drill-down */}
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/:clientId" element={<ClientDetail />} />
              {/* TASK-579: removed stale /risk-profile page route — RiskProfileWizard (in the
                  client's Risk Profile tab) is the single live entry point. */}
              {/* TASK-373: KYC Workspace (split-screen AI-assisted filling) */}
              <Route path="/clients/:clientId/kyc-workspace" element={<KYCWorkspace />} />
              <Route path="/compliance" element={<CompliancePage />} />
              {/* TASK-374: Compliance Officer Review */}
              <Route path="/compliance/review-queue" element={<COReviewQueue />} />
              <Route path="/compliance/review/:reviewId" element={<COReviewWorkspace />} />
              {/* TASK-164 Phase 2: New routes for UX refinement */}
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              {/* TASK-440/450: Document Workbench — format-dispatched editor (lazy) */}
              <Route
                path="/workbench/:docId"
                element={
                  <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading workbench…</div>}>
                    <WorkbenchRouter />
                  </Suspense>
                }
              />
              {/* TASK-265: Unified Search */}
              <Route path="/search" element={<SearchPage />} />
              {/* TASK-359: Task Management */}
              <Route path="/tasks" element={<TaskListPage />} />
              {/* TASK-702: a task's email_draft artifact resolves here — the link the review
                  card offers must land on a real surface, not the not-found page. */}
              <Route path="/drafts/:draftId" element={<DraftPage />} />
              {/* TASK-703: a task's regulated_verdict artifact resolves here. */}
              <Route path="/verdicts/:verdictId" element={<VerdictPage />} />
              {/* TASK-168: Settings page */}
              <Route path="/settings" element={<SettingsPage />} />
              {/* TASK-644: Meetings list */}
              {/* TASK-666: the meetings page is now the MeetingsList canvas widget. This route
                  survives only to redirect bookmarks; it summons the tile then replaces to /dashboard. */}
              <Route path="/meetings" element={<MeetingsRedirect />} />
              {/* TASK-393: New 3-screen meeting flow */}
              {/* TASK-673: explicit start + capture-mode chooser (reminder alert target). */}
              <Route path="/meeting/:id/start" element={<StartMeetingPage />} />
              {/* TASK-672: overdue-reminder recovery actions summon the list + open the modal */}
              <Route path="/meeting/:id/reschedule" element={<StartMeetingPage action="reschedule" />} />
              <Route path="/meeting/:id/cancel" element={<StartMeetingPage action="cancel" />} />
              <Route path="/meeting/:id/prepare" element={<PreparationPage />} />
              <Route path="/meeting/:id/live" element={<LiveMeetingPage />} />
              <Route path="/meeting/:id/review" element={<PostMeetingPage />} />
              {/* TASK-398: Redirects for old bookmarks */}
              <Route path="/meeting/:id/wizard" element={<RedirectWithParams to="/meeting/:id/prepare" />} />
              <Route path="/data-gathering/:id" element={<RedirectWithParams to="/meeting/:id/prepare" />} />
              <Route path="/meeting/:id" element={<RedirectWithParams to="/meeting/:id/live" />} />
              <Route path="/meeting" element={<MeetingsRedirect />} />
              {/* TASK-549: dev-only test routes; import.meta.env.DEV is statically
                  false in prod builds, so these + their imports tree-shake out */}
              {import.meta.env.DEV && (
                <>
                  <Route path="/clients-test" element={<ClientListTest />} />
                  <Route path="/websocket-test" element={<WebSocketTestPage />} />
                  <Route path="/form-builder-test" element={<FormBuilderTest />} />
                  <Route path="/language-test" element={<LanguageTestPage />} />
                </>
              )}
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
          </Routes>
        </LanguageProvider>
      </AccountProvider>
  )
}

export default App